// Vue & Plugins
import { computed, ComputedRef, ref, Ref, watch } from 'vue'
import { defineStore } from 'pinia'

// Models
import { Site } from '../sites/models/site'
import { SiteConfig } from '../sites/models/siteConfig'
import { DEFAULT_PER_PAGE } from '../common/models/pagination'

// Services
import { fetch } from './authSiteService'
import { SitePermission } from '../sites/models/sitePermission'
import { show, showConfig, showUserPermissions } from '../sites/siteService'
import { SiteTerms } from '../sites/models/siteTerms'

// Data
export const useAuthSiteStore = defineStore(
    'AuthSiteStore',
    () => {
        const selected: Ref<Site | null> = ref(null)

        const sites: Ref<Site[]> = ref([])
        const isLoading = ref(false)
        const fetchSites = async () => {
            const result = await fetch({ name: '' }, DEFAULT_PER_PAGE, 1)

            sites.value = result.sites

            if (selected.value === null) {
                selectDefaultSite()
            }
        }

        const current: Ref<Site | null> = ref(null)
        const currentIsLoading = ref(false)
        const fetchCurrentSite = async () => {
            const result = await show((selected.value as Site).id)

            current.value = result.site
        }

        const currentConfig: Ref<SiteConfig | null> = ref(null)
        const currentConfigIsLoading = ref(false)
        const fetchCurrentSiteConfig = async () => {
            const result = await showConfig(current.value as Site)

            currentConfig.value = result.siteConfig
        }

        const currentPermissions: Ref<SitePermission[]> = ref([])
        const currentPermissionsIsLoading = ref(false)
        const fetchCurrentSitePermissions = async () => {
            const result = await showUserPermissions(current.value as Site)

            currentPermissions.value = result.sitePermissions
        }

        const terms: ComputedRef<SiteTerms | null> = computed(() => currentConfig.value?.terms ?? null)

        watch(current, (newSite, oldSite) => {
            // TODO: We're only watching if the old site wasn't null because the config and permissions are fetched and awaited in the navigation guard on first page load
            if (oldSite !== null && newSite !== null) {
                fetchCurrentSiteConfig()
                fetchCurrentSitePermissions()
            }
        })

        const selectDefaultSite = () => {
            // TODO: We could save last used site in local storage and use that to select the default site
            if (sites.value.length > 0) {
                selected.value = sites.value[0]
            } else {
                // If no sites are available, we set the selected site to null
                selected.value = null
            }
        }

        const timeZoneId = computed(() => { return currentConfig.value?.settings.timezone ?? 'Unknown' })

        const approvalRequiredDefault = computed(() => { return currentConfig.value?.settings.shiftWizard.form.approvalRequired.default ?? false })
        const approvalRequiredEditable = computed(() => { return currentConfig.value?.settings.shiftWizard.form.approvalRequired.editable ?? true })

        const openDefault = computed(() => { return currentConfig.value?.settings.shiftWizard.form.open.default ?? false })

        const reset = () => {
            sites.value = []
            selected.value = null
            current.value = null
            currentConfig.value = null
            currentPermissions.value = []
        }

        return {
            isLoading,
            sites,
            selected,
            current,
            currentIsLoading,
            currentConfig,
            currentConfigIsLoading,
            currentPermissions,
            currentPermissionsIsLoading,
            terms,
            timeZoneId,
            approvalRequiredDefault,
            approvalRequiredEditable,
            openDefault,
            fetchSites,
            fetchCurrentSite,
            fetchCurrentSiteConfig,
            fetchCurrentSitePermissions,
            reset,
            selectDefaultSite,
        }
    },
)
